"use client";

import { useEffect, useState } from "react";
import { useLoading } from "@/context/LoadingContext";

function DotPulse() {
  return (
    <div className="flex items-center justify-center gap-1.5" aria-hidden>
      <span className="h-2.5 w-2.5 animate-[apt-dot_1s_ease-in-out_infinite] rounded-full bg-amber-400 [animation-delay:0ms]" />
      <span className="h-2.5 w-2.5 animate-[apt-dot_1s_ease-in-out_infinite] rounded-full bg-indigo-400 [animation-delay:150ms]" />
      <span className="h-2.5 w-2.5 animate-[apt-dot_1s_ease-in-out_infinite] rounded-full bg-emerald-400 [animation-delay:300ms]" />
    </div>
  );
}

function StepRow({ step }: { step: { key: string; label: string; status: string } }) {
  const icon =
    step.status === "completed" ? (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/25 text-emerald-300 ring-1 ring-emerald-500/40">
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    ) : step.status === "error" ? (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-500/25 text-rose-300 ring-1 ring-rose-500/40">
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    ) : step.status === "active" ? (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400/20 text-amber-200 ring-2 ring-amber-400/50">
        <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" />
      </span>
    ) : (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-slate-500 ring-1 ring-slate-700">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
      </span>
    );

  return (
    <li
      className={`flex items-start gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
        step.status === "active"
          ? "bg-slate-800/90 ring-1 ring-amber-500/30"
          : step.status === "completed"
            ? "opacity-90"
            : step.status === "error"
              ? "bg-rose-950/40 ring-1 ring-rose-500/25"
              : "opacity-60"
      }`}
    >
      {icon}
      <span
        className={`pt-0.5 text-[13px] leading-snug ${
          step.status === "active" ? "font-semibold text-slate-50" : "text-slate-300"
        }`}
      >
        {step.label}
      </span>
    </li>
  );
}

export default function GlobalLoader() {
  const {
    isLoading,
    loadingMessage,
    loadingMode,
    progressTitle,
    progressValue,
    progressSteps,
  } = useLoading();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setMounted(true);
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), 320);
    return () => window.clearTimeout(t);
  }, [isLoading]);

  if (!mounted) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity duration-300 ease-out ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      aria-live="polite"
      aria-busy={visible}
      role="status"
    >
      <div
        className={`absolute inset-0 bg-slate-950/72 backdrop-blur-md transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        className={`relative z-10 w-full max-w-md rounded-3xl border border-slate-700/80 bg-slate-900/95 px-6 py-8 shadow-2xl shadow-black/50 ring-1 ring-white/5 transition-all duration-300 ease-out sm:max-w-lg sm:px-8 ${
          visible ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.98] opacity-0"
        }`}
      >
        {loadingMode === "progress" ? (
          <div className="space-y-5">
            <div>
              <p className="font-heading text-lg font-semibold tracking-tight text-slate-50 sm:text-xl">
                {progressTitle || "Working on your report"}
              </p>
              <p className="mt-2 text-sm text-slate-400">{loadingMessage}</p>
            </div>
            <div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-800 ring-1 ring-slate-700/80">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 via-indigo-500 to-emerald-500 transition-[width] duration-500 ease-out"
                  style={{ width: `${progressValue}%` }}
                />
              </div>
              <p className="mt-1.5 text-right text-[11px] font-medium tabular-nums text-slate-500">{progressValue}%</p>
            </div>
            <ul className="max-h-[min(52vh,22rem)] space-y-1 overflow-y-auto pr-1 text-slate-200">
              {progressSteps.map((s) => (
                <StepRow key={s.key} step={s} />
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-6 h-14 w-14">
              <div className="absolute inset-0 rounded-full border-2 border-slate-700 border-t-amber-400 border-r-indigo-400 animate-spin" />
              <div className="absolute inset-2 rounded-full bg-slate-900/80" />
            </div>
            <DotPulse />
            <p className="mt-5 max-w-xs text-sm font-medium text-slate-200">{loadingMessage}</p>
            <p className="mt-2 text-xs text-slate-500">Aptinomic</p>
          </div>
        )}
      </div>
    </div>
  );
}
